import React, { useState } from 'react';
import { createPortal } from 'react-dom';
import { Button, SparkIcon, Text } from '@spark';
import '@/features/trainings/styles/train.css';

function clampedPercent(percent) {
    if (typeof percent !== 'number' || Number.isNaN(percent)) return null;
    return Math.min(100, Math.max(0, Math.round(percent)));
}

/**
 * Docked bottom-right progress card (Figma KcQvkthzkvYFRqHHj0hCo4
 * 3730:8169 expanded / 3730:7924 collapsed): 40px icon + title/tag +
 * chevron/trash when expanded; 24px icon + title + (n%) when collapsed.
 * Always sticky to the viewport bottom-right (24px from the right edge,
 * flush with the bottom).
 */
const ActiveProgressCard = ({
    iconSlug,
    title,
    tag = null,
    percent = null,
    children = null,
    deleteAriaLabel,
    confirmOpen = false,
    onDeleteClick,
    confirmDialog = null,
    closeAreaRef,
}) => {
    const [collapsed, setCollapsed] = useState(false);
    const displayPercent = clampedPercent(percent);

    const card = (
        <div
            className={[
                'training-progress-banner',
                'fs-unmask',
                collapsed
                    ? 'training-progress-banner--collapsed'
                    : 'training-progress-banner--expanded',
            ].join(' ')}
            role="status"
        >
            <span className="training-progress-banner__icon" aria-hidden="true">
                <SparkIcon slug={iconSlug} size={collapsed ? 'medium' : 'large'} />
            </span>
            <div className="training-progress-banner__content">
                <div className="training-progress-banner__title-row">
                    <div className="training-progress-banner__title">
                        <Text variant="base-semibold">{title}</Text>
                        {collapsed && displayPercent != null ? (
                            <Text
                                variant="detail-regular"
                                color="secondary"
                                className="training-progress-banner__percent"
                            >
                                {`(${displayPercent}%)`}
                            </Text>
                        ) : null}
                        {!collapsed ? tag : null}
                    </div>
                    <div className="training-progress-banner__controls" ref={closeAreaRef}>
                        <Button
                            variant="ghost"
                            size="tiny"
                            type="button"
                            aria-label={collapsed ? 'Expand' : 'Collapse'}
                            aria-expanded={!collapsed}
                            onClick={() => setCollapsed((isCollapsed) => !isCollapsed)}
                            icon={(
                                <SparkIcon
                                    slug={collapsed ? 'chevron-up' : 'chevron-down'}
                                    size="small"
                                />
                            )}
                        />
                        <Button
                            variant="ghost"
                            size="tiny"
                            type="button"
                            aria-label={deleteAriaLabel}
                            disableWhenReadOnly
                            onClick={onDeleteClick}
                            icon={<SparkIcon slug="trash" size="small" />}
                        />
                        {confirmOpen ? confirmDialog : null}
                    </div>
                </div>
                {!collapsed ? children : null}
            </div>
        </div>
    );

    if (typeof document === 'undefined') return card;
    return createPortal(card, document.body);
};

export default ActiveProgressCard;
